import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  filterTypes,
  findingsQueryParams,
} from "../../../Findings/FindingsUtils";
import { FiltersContext } from "../FilterProvider/FilterProvider";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { getFilterJson, processFilter } from "../../AppViewUtils";
import "./FilterPanel.scss";
import "../FiltersMenu/FiltersMenu.scss";
import { showToast } from "../../../Forms/Dialogs/Toast/Toast";
import { MutationTuple, useApolloClient } from "@apollo/client";
import {
  SAVE_FILTER,
  UPDATE_FILTER_V3,
  type SaveFilterResponse,
  type SaveFilterVars,
  type UpdateFilterResponse,
  type UpdateFilterVars,
} from "../../../Findings/FindingsFilters/FindingsFilterApi";
import FilterSaveAs from "../../../Findings/FindingsFilters/FilterSaveAs/FilterSaveAs";
import useRecentFilters from "../useRecentFilters";
import useFiltersData, {
  FILTER_OPTIONS_ERROR_MESSAGE,
} from "../../useFiltersData";
import useFindingsQueryParam from "../../../Findings/FindingsData/useFindingsQueryParam";
import ModalActionButtons from "./ModalActionButtons";
import ConditionRowsPanel from "../../FiltersBar/ConditionRowsPanel/ConditionRowsPanel";
import useStatuses from "../../../Findings/FindingDetailsPane/FindingDetailsPaneHeader/Statuses/useStatuses";
import DependenciesModal from "../../Dependencies/DependenciesModal";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../General/AppPreferencesProvider";
import { AuthContext } from "../../../Auth/AuthContext";
import {
  FieldData,
  FilterObject,
  FiltersToPin,
  FilterPanel,
} from "../CommonFilterTypes";
import { filterEntityEnum, type FilterEntity } from "../filterEntity";
import useSavedFilterType from "../useSavedFilterType";
import { FINDING_ACTUAL_STATUS_FILTER_KEY } from "../filterKey";
import { AppPreferencesContextType } from "../../../types/AppPreferencesProvider";
import { AuthContextType } from "../../../types/AuthProvider";
import {
  ModalContextType,
  DotLoader,
  useMutationWrapper,
  ModalContext,
  ModalButtonsContainer,
  TextButton,
  showErrorDialog,
} from "@SeemplicityDev/shared";

type PinnedConditionValue = { key: string; type: string }[] | string[];

type PinnedDefaultConditions = Record<
  string,
  { field: string; condition: string; value: PinnedConditionValue }
>;

const pinnedDefaultConditions: PinnedDefaultConditions = {
  actual_status: {
    field: "actual_status",
    condition: "in",
    value: [{ key: "OPEN", type: "category" }],
  },
  system_status_key: {
    field: "system_status_key",
    condition: "in",
    value: ["open"],
  },
};

const remediationItemPinnedDefaultConditions: PinnedDefaultConditions = {
  ...pinnedDefaultConditions,
  actual_status: {
    ...pinnedDefaultConditions.actual_status,
    field: "remediation_item.actual_status",
  },
};

const seemplicityFindingPinnedDefaultConditions: PinnedDefaultConditions = {
  ...pinnedDefaultConditions,
  [FINDING_ACTUAL_STATUS_FILTER_KEY]: {
    ...pinnedDefaultConditions.actual_status,
    field: FINDING_ACTUAL_STATUS_FILTER_KEY,
  },
};

interface FormData {
  filterPanels: FilterPanel[];
  saveAsFilterName?: string;
  [key: string]: any; // todo: temporary fix for the types
}

interface FilterPanelProps {
  hasEditMode?: boolean;
  readOnly?: boolean;
  hideSaveAs?: boolean;
  setDefaultsForScopeAndFilter?: boolean;
  enableSaveFilters?: boolean;
  onFilterSave?: (filter: FilterObject) => void;
  showFilterActions?: boolean;
  onEmptyFilterPanel?: () => void;
  entity?: FilterEntity;
  fieldsToIgnore?: string[];
  isInline?: boolean;
  fieldsToIgnoreWarningMsg?: string;
  filtersToPin?: FiltersToPin;
  fieldsToHide?: string[];
  unsupportedFilterFields?: string[];
  // Set to true in contexts that do not have an Apply button (e.g. Rules /
  // Automation Wizard edit-filter modals), where Save/Save As must remain
  // enabled even when pending unapplied changes exist.
  allowSaveWithPendingChanges?: boolean;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  hasEditMode = false,
  readOnly = false,
  hideSaveAs = false,
  setDefaultsForScopeAndFilter = false,
  enableSaveFilters = true,
  onFilterSave = () => {},
  showFilterActions = false,
  onEmptyFilterPanel = () => {},
  entity: catalogEntity,
  fieldsToIgnore = [],
  isInline = false,
  fieldsToIgnoreWarningMsg,
  filtersToPin,
  fieldsToHide = [],
  unsupportedFilterFields = [],
  allowSaveWithPendingChanges = false,
}) => {
  const entity = catalogEntity ?? filterEntityEnum.REMEDIATION_ITEM;
  const [isSaving, setIsSaving] = useState(false);

  const { isAllowed, isSelfActionAllowed } = useContext(
    AppPreferencesContext
  ) as AppPreferencesContextType;

  const {
    filtersState: {
      isEditFilter,
      selectedFilter,
      shouldBlinkField,
      shouldOpenField,
      filterToExecute,
      pendingFilterToExecute: pendingFilterToExecuteState,
      defaultFilter,
    },
    updateFiltersState,
    addUsedField,
    removeUsedField,
    lsFilterKey,
    filtersEntity,
  } = useContext(FiltersContext);
  const savedFilterType = useSavedFilterType(filtersEntity);

  const { openModal, closeModal } = useContext(
    ModalContext
  ) as ModalContextType;

  const client = useApolloClient();

  const {
    authState: { username },
  } = useContext(AuthContext) as AuthContextType;

  const { statuses } = useStatuses();

  // UPDATE_FILTER_V3 returns no filter node, so the cached list needs a refetch.
  const [updateFilter] = useMutationWrapper(UPDATE_FILTER_V3, {
    refetchQueries: ["filters"],
  }) as MutationTuple<UpdateFilterResponse, UpdateFilterVars>;

  const [saveFilter] = useMutationWrapper(SAVE_FILTER, {
    refetchQueries: ["filters", "scopes"],
  }) as MutationTuple<SaveFilterResponse, SaveFilterVars>;

  const { addFilterToRecent } = useRecentFilters();
  const { clearQueryParam, search } = useFindingsQueryParam(null);

  const {
    fieldsData: fieldsDataBeforeEnrichment,
    fieldsLoading,
    hasFieldsError,
  } = useFiltersData({
    filterType: filterTypes.FILTER,
    filterContext: catalogEntity,
  });

  const pinnedFieldNames = useMemo(
    () => filtersToPin?.map((f) => f.filterField) ?? [],
    [filtersToPin]
  );

  const pinnedConditions = useMemo(() => {
    const defaultConditions =
      entity === filterEntityEnum.REMEDIATION_ITEM
        ? remediationItemPinnedDefaultConditions
        : entity === filterEntityEnum.SEEMPLICITY_FINDING
          ? seemplicityFindingPinnedDefaultConditions
          : pinnedDefaultConditions;

    return (filtersToPin ?? [])
      .filter((p) => defaultConditions[p.filterField])
      .map((p) => defaultConditions[p.filterField]);
  }, [filtersToPin, entity]);
  const fieldsData: FieldData[] = fieldsDataBeforeEnrichment
    .filter(
      (field: FieldData) =>
        !fieldsToHide.includes(field.field) ||
        pinnedFieldNames.includes(field.field)
    )
    .map((field: FieldData) => {
      if (fieldsToIgnore.includes(field.field)) {
        return {
          ...field,
          disabled: true,
          warning:
            fieldsToIgnoreWarningMsg ||
            "This filter is not applicable for data validation results and will be ignored",
        };
      }
      return field;
    });

  const {
    formState: { isValid, isDirty },
    control,
    reset,
    getValues,
    handleSubmit,
    setValue,
  } = useFormContext<FormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filterPanels",
  });

  const watchFieldArray = useWatch({
    control,
    name: `filterPanels`,
  });

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const saveAsFilterNameValue = useWatch({
    control,
    name: `saveAsFilterName`,
  });

  const isLoading = fieldsLoading || !selectedFilter || !fields?.length;

  useEffect(() => {
    const queryToParse =
      selectedFilter?.value?.isDirty && filterToExecute
        ? filterToExecute
        : selectedFilter?.value?.query;
    let filter = null;
    try {
      filter = queryToParse ? JSON.parse(queryToParse) : null;
    } catch {
      filter = null;
    }

    let filterPanels: FilterPanel[] = [{ conditions: [] }];

    if (filter) {
      const processedFilter = processFilter(filter);
      filterPanels = processedFilter.filterConditions;
    }

    if (selectedFilter?.value?.isDirty) {
      setValue("filterPanels", filterPanels, { shouldDirty: true });
    } else {
      reset(
        {
          ...getValues(),
          filterPanels,
        },
        { keepIsValid: true }
      );
    }
  }, [selectedFilter, setValue, reset, getValues]);

  const updateDisabled = useMemo(() => {
    return (
      selectedFilter?.meta?.system ||
      !selectedFilter?.value?.editable ||
      !selectedFilter?.value?.id
    );
  }, [selectedFilter]);

  const previewFilter = (editMode?: boolean): void => {
    const data = getValues();
    const filterJson = getFilterJson(data);
    if (isDirty) {
      addFilterToRecent(filterJson);
    }
    // Pending changes do not trigger backend queries, write to localStorage,
    // or reset the findings list — those only happen on Apply.
    // Only forward isEditFilter when the caller explicitly passes editMode —
    // FilterChip calls previewFilter() with no args and must not exit edit mode.
    updateFiltersState({
      pendingFilterToExecute: filterJson,
      ...(editMode !== undefined && { isEditFilter: editMode }),
    });
  };

  const validateOnSave = (): void => {
    handleSubmit((data: FormData) => onSave(data, false))();
  };

  const onSave = (data: FormData, confirmed: boolean): void => {
    const filter = getFilterJson(data);
    const name = selectedFilter?.label;
    const id = selectedFilter?.value?.id;

    // The V3 document declares $id/$name non-null; if the selected filter is missing
    // (shouldn't happen — Save is only reachable with one selected) bail with a fixed
    // message rather than let Apollo's variable-validation error surface GraphQL
    // internals (operation name, field paths) to the user via the .catch below.
    if (!id || !name) {
      showErrorDialog({ message: "Couldn't save this filter." });
      return;
    }

    setIsSaving(true);
    updateFilter({
      variables: {
        id,
        name,
        filterData: filter,
        filterType: savedFilterType,
        confirmed,
      },
    })
      .then((res) => {
        const payload = res?.data?.update_finding_filter_v3;

        // Criteria change blocked by dependencies: show the Save Filter modal —
        // Cancel / Save a New Filter / Save and Update (re-submit with confirmed).
        // Rendered via openModal (in-tree) so the dependency links keep their
        // react-router context — showDialog renders in a detached root without it.
        if (
          !confirmed &&
          !payload?.ok &&
          !!payload?.dependencies?.total_count
        ) {
          openModal({
            title: "Save Filter",
            size: "m",
            className: "filter-dependencies-save-dialog",
            overlay: true,
            component: (
              <div className={"delete-filter-modal"}>
                <DependenciesModal
                  variant={"edit"}
                  entityName={name}
                  dependencies={payload.dependencies}
                />
                <ModalButtonsContainer spaceBetween>
                  <TextButton
                    buttonStyle={"textLink"}
                    label={"Cancel"}
                    onClick={closeModal}
                    data-testid={"cancel-save-filter"}
                  />
                  <div className={"filter-dependencies-save-actions"}>
                    {!hideSaveAs && (
                      <TextButton
                        buttonStyle={"secondary"}
                        label={"Save a New Filter"}
                        onClick={openSaveAsModal}
                        data-testid={"save-as-new-filter"}
                      />
                    )}
                    <TextButton
                      buttonStyle={"primary"}
                      label={"Save and Update"}
                      onClick={() => {
                        closeModal();
                        handleSubmit((d: FormData) => onSave(d, true))();
                      }}
                      data-testid={"save-and-update"}
                    />
                  </div>
                </ModalButtonsContainer>
              </div>
            ),
          });
        } else if (payload?.ok) {
          const nextSelectedFilter: FilterObject = {
            label: name || "",
            value: {
              query: filter,
              id,
              editable: true,
            },
            meta: {
              created_by: username,
            },
          };

          updateFiltersState({
            filterToExecute: filter,
            pendingFilterToExecute: undefined,
            selectedFilter: nextSelectedFilter,
            isEditFilter: false,
          });
          if (setDefaultsForScopeAndFilter) {
            localStorage.setItem(
              lsFilterKey,
              JSON.stringify(nextSelectedFilter)
            );
          }
          client.refetchQueries({ include: ["filters"] });
          onFilterSave(nextSelectedFilter);
          showToast({
            message: (
              <div className={"modal-text"}>
                The Filter{" "}
                <span className={"modal-live-text"}>
                  {nextSelectedFilter.label}
                </span>{" "}
                was updated successfully
              </div>
            ),
          });
        } else {
          showErrorDialog({ message: "Filter update failed" });
        }
      })
      .catch((err: Error) => showErrorDialog({ message: err.message }))
      .finally(() => {
        setIsSaving(false);
      });
  };

  const onSaveAs = (data: FormData): void => {
    // Unreachable in practice — the effect below only submits once a name is set.
    if (!saveAsFilterNameValue) {
      return;
    }

    const filter = getFilterJson(data);

    saveFilter({
      variables: {
        name: saveAsFilterNameValue,
        filterData: filter,
        filterType: savedFilterType,
      },
    })
      .then((res) => {
        const newFilter = res.data?.new_finding_filter_v2?.finding_filter;

        if (newFilter) {
          const selectedFilter: FilterObject = {
            label: newFilter.name,
            value: {
              query: newFilter.filter_data,
              id: newFilter.id,
              editable: true,
            },
            meta: {
              created_by: username,
            },
          };

          if (!!search) {
            clearQueryParam([
              findingsQueryParams.FILTER_ID,
              findingsQueryParams.FILTER_TO_EXECUTE,
            ]);
          }

          updateFiltersState({
            filterToExecute: filter,
            pendingFilterToExecute: undefined,
            selectedFilter,
            isEditFilter: false,
          });
          if (setDefaultsForScopeAndFilter) {
            localStorage.setItem(lsFilterKey, JSON.stringify(selectedFilter));
          }

          onFilterSave(selectedFilter);
          closeModal();
          showToast({
            message: (
              <div className={"modal-text"}>
                The Filter{" "}
                <span className={"modal-live-text"}>{newFilter.name}</span> was
                saved successfully
              </div>
            ),
            "data-testid": "filter-save-as-toast",
          });
          setValue("saveAsFilterName", "");
        }
      })
      .catch((ex: Error) => showErrorDialog({ message: ex.message }));
  };

  const openSaveAsModal = (): void => {
    openModal({
      title: "Save As",
      size: "m",
      overlay: true,
      component: (
        <FilterSaveAs
          setValue={setValue}
          type={filterTypes.FILTER.toLowerCase()}
          filter_data={pendingFilterToExecuteState ?? filterToExecute}
          scopesList={[]}
          location="filter-panel"
          name=""
        />
      ),
    });
  };

  useEffect(() => {
    if (!!saveAsFilterNameValue) {
      handleSubmit(onSaveAs)();
    }
  }, [saveAsFilterNameValue, handleSubmit]);

  const onCancelHandler = (): void => {
    const originalQuery = selectedFilter?.value?.id
      ? selectedFilter?.value?.query
      : undefined;
    let filter = null;
    try {
      filter = originalQuery ? JSON.parse(originalQuery) : null;
    } catch {
      filter = null;
    }
    let filterPanels: FilterPanel[] = [{ conditions: [] }];
    if (!!filter) {
      const processedFilter = processFilter(filter);
      filterPanels = processedFilter.filterConditions;
    }

    // If the reset would be empty but pinned conditions exist, seed them in now
    // so the single reset() call below is already the correct final state.
    const hasConditions = filterPanels.some((p) => !!p.conditions?.length);
    if (!hasConditions && !!pinnedConditions?.length) {
      filterPanels = [{ conditions: [...pinnedConditions] }];
    }

    reset({ ...getValues(), filterPanels }, { keepIsValid: true });

    setTimeout(() => {
      // For saved filters use the original saved query; for ad-hoc derive from
      // the actual reset form state so the useEffect re-run restores the same panels.
      const cleanQuery = selectedFilter?.value?.id
        ? originalQuery
        : getFilterJson(getValues());

      const cleanFilter: FilterObject = {
        label: selectedFilter?.label ?? "",
        value: {
          query: cleanQuery,
          id: selectedFilter?.value?.id,
          editable: selectedFilter?.value?.editable,
        },
        meta: selectedFilter?.meta,
      };

      // Cancel reverts to the server-saved filter and clears pending state.
      // Also set filterToExecute to the clean query so the backend query fires
      // and results snap back to the server-saved state immediately.
      updateFiltersState({
        filterToExecute: cleanQuery,
        selectedFilter: cleanFilter,
        pendingFilterToExecute: undefined,
        isEditFilter: false,
      });
      // Always write the clean state back so a previous dirty LS entry is cleared
      // (needed for pages where setDefaultsForScopeAndFilter=false, e.g. DV)
      localStorage.setItem(
        lsFilterKey,
        JSON.stringify({ ...cleanFilter, filterToExecute: cleanQuery })
      );
    }, 0);
  };

  // Disable Save/Save As while there are pending unapplied changes — the user
  // must Apply (and thus review) before persisting a filter configuration.
  // Contexts without an Apply button (Rules / Automation Wizard edit-filter
  // modals) opt out via `allowSaveWithPendingChanges`.
  const isSaveDisabledByPendingChanges =
    !allowSaveWithPendingChanges &&
    pendingFilterToExecuteState !== null &&
    pendingFilterToExecuteState !== undefined;

  const disableSave = useMemo(
    () =>
      updateDisabled ||
      !isValid ||
      !isDirty ||
      !enableSaveFilters ||
      isSaveDisabledByPendingChanges ||
      !(
        isAllowed(userActionsEnum.EDIT_FILTER) ||
        isSelfActionAllowed(
          userActionsEnum.SELF_EDIT_FILTER,
          selectedFilter?.meta?.created_by
        )
      ),
    [
      updateDisabled,
      isValid,
      isDirty,
      enableSaveFilters,
      isSaveDisabledByPendingChanges,
      selectedFilter,
      isAllowed,
      isSelfActionAllowed,
    ]
  );

  const disableSaveAs = useMemo(
    () => !isValid || !enableSaveFilters || isSaveDisabledByPendingChanges,
    [isValid, enableSaveFilters, isSaveDisabledByPendingChanges]
  );

  const saveTooltipContent = isSaveDisabledByPendingChanges
    ? "Apply your changes before saving"
    : undefined;

  const showEditFilterButton =
    hasEditMode &&
    !isEditFilter &&
    selectedFilter?.value?.editable &&
    !readOnly &&
    isAllowed(userActionsEnum.EDIT_FILTER);

  const onEditHandler = (): void => updateFiltersState({ isEditFilter: true });

  const allowEditSelectedFilter =
    !readOnly &&
    ((hasEditMode && isEditFilter) || !hasEditMode) &&
    (selectedFilter?.value?.editable ||
      selectedFilter?.value?.id === defaultFilter?.value?.id);

  const showInnerInputActions =
    (!hasEditMode || isEditFilter) && !readOnly && !showFilterActions;

  const allowAddFilter = allowEditSelectedFilter && isValid && !readOnly;

  const disableAddCondition =
    !isValid ||
    !controlledFields?.[controlledFields?.length - 1]?.conditions?.length;

  const addCondition = (): void => {
    append({
      conditions: [
        ...pinnedConditions,
        { field: null, condition: null, value: "" },
      ],
    });
  };

  return (
    <form
      className={`panel-data-container panel-data-container-filters panel-data-container-filters-FormProvider`}
      onSubmit={handleSubmit(validateOnSave)}
    >
      <div
        className={`panel-data-wrap ${isLoading ? "is-loading" : ""}`}
        data-testid={"filter-panel"}
      >
        {hasFieldsError ? (
          <div className={"chips-rows-wrap"}>
            <div
              className={"filter-panel-error"}
              data-testid={"filter-panel-error"}
            >
              <i className={"seem-icon seem-icon-error"} />
              {FILTER_OPTIONS_ERROR_MESSAGE}
            </div>
          </div>
        ) : isLoading ? (
          <div className={"chips-rows-wrap"}>
            <DotLoader className={"filter-panel-loader"} />
          </div>
        ) : (
          <ConditionRowsPanel
            entity={entity}
            disableSave={disableSave}
            showSaveButtons={
              showInnerInputActions &&
              isAllowed(userActionsEnum.CREATE_FILTER) &&
              (entity === filterEntityEnum.REMEDIATION_ITEM ||
                savedFilterType === filterTypes.SEEMPLICITY_FINDING_FILTER)
            }
            disableSaveAs={disableSaveAs}
            saveTooltipContent={saveTooltipContent}
            isDirty={isDirty}
            showAddCondition={allowAddFilter}
            reset={onCancelHandler}
            saveAs={openSaveAsModal}
            previewFilter={previewFilter}
            onAddConditions={addCondition}
            onLastConditionRowRemove={onEmptyFilterPanel}
            disableAddCondition={disableAddCondition}
            controlledFields={controlledFields}
            remove={remove}
            showCancelButton={showInnerInputActions}
            selectedId={selectedFilter?.value?.id}
            fieldsData={fieldsData}
            context={filterTypes.FILTER}
            isEditable={allowEditSelectedFilter}
            onEditHandler={onEditHandler}
            showEditButton={showEditFilterButton}
            location={"filter-panel"}
            showAddValue={allowAddFilter}
            allowRemoveAllConditions={
              entity === filterEntityEnum.RESOURCE ||
              entity === filterEntityEnum.DATA_VALIDATION_FINDING
            }
            // statuses from useStatuses (still JS) is null until loaded;
            // cast through unknown preserves the null value at runtime.
            statusesData={statuses as unknown as unknown[]}
            shouldBlinkField={shouldBlinkField}
            shouldOpenField={shouldOpenField}
            updateFilter={updateFiltersState}
            isSaving={isSaving}
            showDelete={
              isEditFilter || (hasEditMode ? showEditFilterButton : true)
            }
            chipPopoverClassName={`filter-panel-chip ${isInline ? "is-inline" : ""}`}
            filtersToPin={filtersToPin}
            unsupportedFilterFields={unsupportedFilterFields}
            addUsedField={addUsedField}
            removeUsedField={removeUsedField}
          />
        )}
        {showFilterActions && isEditFilter && (
          <ModalActionButtons
            disableSave={disableSave}
            disableSaveAs={disableSaveAs}
            cancelEdit={hasEditMode && isEditFilter}
            isValid={isValid}
            onCancelHandler={onCancelHandler}
            hideSaveAs={hideSaveAs}
            validateOnSave={validateOnSave}
            openSaveAsModal={openSaveAsModal}
            saveTooltipContent={saveTooltipContent}
          />
        )}
      </div>
    </form>
  );
};

export default FilterPanel;
